import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconReplace } from '@components/icons/replace';
import { IconRestart } from '@components/icons/restart';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useScopedTranslate } from '@locale/translate';

import { DomainType, EmailAddress } from 'api/company/requests';

type AddressRowProps = {
  address: EmailAddress;
  domainType?: DomainType;
  onEdit: () => void;
  onVerify: () => void;
  onSetDefault: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
};

export const AddressRow = ({
  address,
  domainType,
  onEdit,
  onVerify,
  onSetDefault,
  onToggleActive,
  onDelete,
}: AddressRowProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Card spaceX={4} spaceY={3}>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack space={3} orientation="horizontal" align="center">
          <IconEmail />
          <div>
            <Stack orientation="horizontal" space={1} align="center">
              <Text text={address.fromName} font="semibold14" />
            </Stack>
            <Stack orientation="horizontal" space={1} align="center">
              <Text text={address.fromAddress} font="regular14" color="textSecondary" />
              {domainType && (
                <Text text={`• ${domainType}`} font="regular14" color="textSecondary" />
              )}
            </Stack>
            <TextStatusIndicator
              variant={address.isVerified ? 'success' : 'pending'}
              text={address.isVerified ? t('verified') : t('unverified')}
              size="small"
            />
          </div>
        </Stack>
        <Stack orientation="horizontal" align="center" space={2}>
          {address.isDefault && <Badge size="md" variant="indigo" text={t('default')} />}
          {!address.isActive && address.isVerified && (
            <Badge size="md" variant="neutral" text={t('disabled')} />
          )}
          <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
            {!address.isVerified && (
              <Menu.Item label={t('verify')} icon={<IconRestart />} onSelect={onVerify} />
            )}
            <Menu.Item label={t('edit')} icon={<IconEdit />} onSelect={onEdit} />
            <Menu.Item
              label={t('makeDefault')}
              icon={<IconReplace />}
              onSelect={onSetDefault}
              isDisabled={address.isDefault || !address.isVerified || !address.isActive}
            />
            <Menu.Separator />
            <Menu.Switch
              label={t('active')}
              value={address.isActive}
              onChange={onToggleActive}
              isDisabled={address.isDefault}
            />
            <Menu.Separator />
            <Menu.Item
              label={t('remove')}
              icon={<IconTrash />}
              onSelect={onDelete}
              variant="danger"
              isDisabled={address.isDefault}
            />
          </Menu>
        </Stack>
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    default: 'Default',
    verified: 'Verified',
    unverified: 'Unverified',
    disabled: 'Disabled',
    edit: 'Edit',
    makeDefault: 'Make default',
    active: 'Active',
    remove: 'Remove',
    verify: 'Verify',
  },
};
